import { useState, useEffect } from "react";
import {
  Box, Typography, Button as MuiButton, Dialog, Grid,
  DialogTitle, DialogContent, DialogActions, CircularProgress,
  Alert, Switch, Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import pagamentoService from "../../services/pagamentosService.ts";
import planosService from "../../services/planosService.ts";
import handleService from "../../services/apiService.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import { usePlanoContext } from "../../contexts/PlanoContext.tsx";
import { usePlanLimits } from "../../hooks/usePlanLimits.ts";
import { PLANOS_DATA } from "../Planos/index.tsx";
import PagamentoModal from "../../components/Modals/PagamentoModal/index.tsx";
import { AsaasPaymentResponse } from "../../components/Modals/PagamentoModal/types.ts";
import Button from "../../ui/Button.tsx";
import Badge, { BadgeColor } from "../../ui/Badge.tsx";
import ErrorBanner from "../../ui/ErrorBanner.tsx";
import "../../styles/design-system.css";

interface MeuPlanoResponse {
  planoId: string | null;
  planoModeloId: string | null;
  planoNome: string | null;
  planoPreco: number | null;
  valorFaturado: number | null;
  ciclo: string | null;
  cicloLabel: string;
  ativo: boolean;
  pago: boolean;
  dataExpiracao: string | null;
  acessoBloqueado?: boolean;
  trocaAgendada: boolean;
  planoFuturoId: string | null;
  planoFuturoNome: string | null;
  planoFuturoPreco: number | null;
  cicloFuturo: string | null;
  inconsistente: boolean;
  billingType: string | null;
  cancelado: boolean;
}

interface Fatura {
  id: string;
  valor: number;
  status: string;
  metodoPagamento: string | null;
  dataCriacao: string | null;
  dataPagamento: string | null;
  vencimento: string | null;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return new Date(dateStr + "T23:59:59").toLocaleDateString("pt-BR");
};

// Faturas vêm com data/hora completa (dataPagamento/dataCriacao), diferente
// de dataExpiracao (só data) — formatDate acima assume meia-noite fixa e
// bagunçaria o dia em fusos diferentes de UTC-3 se aplicada aqui também.
const formatDateHora = (dateStr: string | null) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("pt-BR");
};


// Ícone + rótulo amigável pro meio de pagamento — só o que a ASAAS/nosso
// backend realmente informam (billingType). Não existe bandeira/final do
// cartão persistido, então não tem como mostrar "•••• 4242" de verdade.
const billingTypeInfo = (billingType: string | null) => {
  switch (billingType) {
    case "PIX":
      return { icon: <QrCode2OutlinedIcon fontSize="small" />, label: "PIX" };
    case "BOLETO":
      return { icon: <ReceiptLongOutlinedIcon fontSize="small" />, label: "Boleto bancário" };
    case "CREDIT_CARD":
      return { icon: <CreditCardOutlinedIcon fontSize="small" />, label: "Cartão de crédito" };
    default:
      return { icon: <CreditCardOutlinedIcon fontSize="small" />, label: "Não definido" };
  }
};

// Status cru da ASAAS -> rótulo + cor amigáveis pra listagem de faturas.
const faturaStatusInfo = (status: string): { label: string; color: BadgeColor } => {
  switch (status) {
    case "RECEIVED":
    case "CONFIRMED":
      return { label: "Pago", color: "green" };
    case "PENDING":
      return { label: "Pendente", color: "orange" };
    case "OVERDUE":
      return { label: "Vencido", color: "red" };
    case "REFUNDED":
      return { label: "Estornado", color: "neutral" };
    default:
      return { label: status, color: "neutral" };
  }
};

const primeiroDiaDoMes = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const hojeIso = () => new Date().toISOString().slice(0, 10);

const Banner: React.FC<{ tone: "info" | "warning" | "success" | "danger"; children: React.ReactNode }> = ({ tone, children }) => {
  const map = {
    info: { bg: "var(--blue-tint)", fg: "var(--blue-hover)" },
    warning: { bg: "var(--orange-tint)", fg: "var(--orange-hover)" },
    success: { bg: "var(--green-tint)", fg: "var(--green)" },
    danger: { bg: "var(--red-tint)", fg: "var(--red)" },
  }[tone];
  return (
    <div style={{ background: map.bg, color: map.fg, padding: "12px 16px", borderRadius: "var(--radius-md)", fontSize: 13.4, fontWeight: 500, marginBottom: 20 }}>
      {children}
    </div>
  );
};

const MeuPlanoPage = () => {
  const [plano, setPlano] = useState<MeuPlanoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [trocaDialogOpen, setTrocaDialogOpen] = useState(false);
  const [trocaCiclo, setTrocaCiclo] = useState<"MONTHLY" | "YEARLY">("MONTHLY");
  const [planosDisponiveis, setPlanosDisponiveis] = useState<any[]>([]);
  const [selectedPlanoId, setSelectedPlanoId] = useState("");
  const [loadingTroca, setLoadingTroca] = useState(false);
  const [cancelAgendamentoOpen, setCancelAgendamentoOpen] = useState(false);
  const [loadingCancelAgendamento, setLoadingCancelAgendamento] = useState(false);
  const [trocaPaymentModalOpen, setTrocaPaymentModalOpen] = useState(false);
  const [trocaPaymentData, setTrocaPaymentData] = useState<AsaasPaymentResponse | null>(null);

  // Dados complementares do painel novo — nenhum deles é crítico pro
  // funcionamento da página, então erro em qualquer um fica só em log,
  // sem travar a tela (mesmo espírito do fetchPlanos de baixo).
  const [faturas, setFaturas] = useState<Fatura[]>([]);
  const [loadingFaturas, setLoadingFaturas] = useState(true);
  const [usuariosCount, setUsuariosCount] = useState<number | null>(null);
  const [cirurgiasMesCount, setCirurgiasMesCount] = useState<number | null>(null);
  const { limits: planLimits } = usePlanLimits();

  const { showFeedback } = useFeedback();
  const { refreshPlano } = usePlanoContext();

  const fetchPlano = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await handleService({ method: MethodsEnum.GET, endpoint: "/plano/meu-plano" });
      setPlano(result?.data || null);
    } catch (err: any) {
      setError(err.message || "Erro ao carregar informações da assinatura.");
      setPlano(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlano();
  }, []);

  useEffect(() => {
    const fetchPlanos = async () => {
      try {
        const result = await planosService.listarPlanos();
        setPlanosDisponiveis(result?.data || []);
      } catch {
        // silencioso: dialog de troca mostra spinner enquanto vazio
      }
    };
    fetchPlanos();
  }, []);

  useEffect(() => {
    if (trocaDialogOpen && plano?.ciclo) {
      setTrocaCiclo(plano.ciclo as "MONTHLY" | "YEARLY");
    }
  }, [trocaDialogOpen, plano]);

  useEffect(() => {
    const fetchFaturas = async () => {
      try {
        const result = await handleService({ method: MethodsEnum.GET, endpoint: "/faturas/minhas" });
        setFaturas(Array.isArray(result?.data) ? result.data : []);
      } catch {
        setFaturas([]);
      } finally {
        setLoadingFaturas(false);
      }
    };
    fetchFaturas();
  }, []);

  useEffect(() => {
    const fetchUso = async () => {
      try {
        const respUsuarios = await handleService({ endpoint: "/usuario/listar" });
        const listaUsuarios = Array.isArray(respUsuarios) ? respUsuarios : respUsuarios?.data || [];
        setUsuariosCount(listaUsuarios.length);
      } catch {
        setUsuariosCount(null);
      }
      try {
        const respCirurgias = await handleService({
          endpoint: "/cirurgia",
          params: { dataInicial: primeiroDiaDoMes(), dataFinal: hojeIso(), size: 1 },
        });
        setCirurgiasMesCount(respCirurgias?.data?.totalElements ?? null);
      } catch {
        setCirurgiasMesCount(null);
      }
    };
    fetchUso();
  }, []);

  const handleTrocarPlano = async () => {
    if (!selectedPlanoId) return;
    setLoadingTroca(true);
    try {
      const result = await pagamentoService.alterarPlano(selectedPlanoId, trocaCiclo);
      setTrocaDialogOpen(false);
      if (result && result.status !== "PLANO_ALTERADO") {
        setTrocaPaymentData(result);
        setTrocaPaymentModalOpen(true);
      } else {
        showFeedback("Troca de plano realizada com sucesso!", "success");
        await fetchPlano();
      }
    } catch (error: any) {
      showFeedback(error.message || "Erro ao trocar de plano", "error");
    } finally {
      setLoadingTroca(false);
    }
  };

  const handleCloseTrocaPaymentModal = () => {
    setTrocaPaymentModalOpen(false);
    setTrocaPaymentData(null);
    fetchPlano();
  };

  const handleCancelarAgendamento = async () => {
    setLoadingCancelAgendamento(true);
    try {
      await pagamentoService.cancelarAgendamentoTroca();
      showFeedback("Troca de plano agendada cancelada com sucesso!", "success");
      setCancelAgendamentoOpen(false);
      await fetchPlano();
    } catch (error: any) {
      showFeedback(error.message || "Erro ao cancelar troca agendada", "error");
    } finally {
      setLoadingCancelAgendamento(false);
    }
  };

  const handleCancelarAssinatura = async () => {
    setLoadingCancel(true);
    try {
      await pagamentoService.cancelarAssinatura();
      setConfirmOpen(false);
      setPlano((prev) => {
        if (prev) {
          showFeedback(`Assinatura cancelada. Seu acesso permanece disponível até ${formatDate(prev.dataExpiracao)}.`, "success");
          return { ...prev, cancelado: true };
        }
        showFeedback("Assinatura cancelada com sucesso!", "success");
        return null;
      });
      refreshPlano();
    } catch (error: any) {
      showFeedback(error.message || "Erro ao cancelar assinatura", "error");
    } finally {
      setLoadingCancel(false);
    }
  };

  if (loading) {
    return (
      <div className="ds" style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ds" style={{ maxWidth: 600, margin: "40px auto" }}>
        <ErrorBanner message={error} />
        <Button variant="outline" onClick={fetchPlano} style={{ width: "100%" }}>Tentar novamente</Button>
      </div>
    );
  }

  if (!plano) {
    return (
      <div className="ds" style={{ textAlign: "center", paddingTop: 60 }}>
        <h2 style={{ color: "var(--ink-soft)" }}>Nenhum plano ativo encontrado.</h2>
        <p style={{ marginTop: 8, color: "var(--ink-faint)" }}>
          Vá em <a href="/planos/listagem" style={{ color: "var(--blue)" }}>Ver Planos</a> para contratar uma assinatura.
        </p>
      </div>
    );
  }

  // Obedece o backend (Empresa.isAcessoBloqueado), que aplica a folga de fim de
  // semana e o fuso de Brasilia. Este isExpired comanda isTrial, hasActivePlan e
  // isCancelled, ou seja, o status inteiro desta tela: recalculando aqui, no
  // sabado de um vencimento na sexta a tela diria "plano expirado" para quem
  // ainda esta com o sistema liberado. Fallback local cobre a janela de deploy.
  const isExpired = typeof plano.acessoBloqueado === "boolean"
    ? plano.acessoBloqueado
    : plano.dataExpiracao ? new Date(plano.dataExpiracao + "T23:59:59") < new Date() : false;
  const isTrial = !plano.planoId && !isExpired;
  // "pago" só vira true quando a ASAAS confirma um pagamento de verdade
  // (handlePagamentoConfirmado, o único setPago(true) de todo o backend) —
  // uma empresa liberada manualmente pelo painel admin (alterarPlano/
  // alterarDataLiberacao) nunca passa por ali, então "pago" fica sempre
  // false mesmo com acesso real liberado (o que controla o acesso de
  // verdade é dataExpiracao, igual o AcessoBloqueadoInterceptor já usa).
  // Sem esse OR, a tela mostraria "plano inativo" pra clientes liberados
  // manualmente com acesso completo ao sistema. Mesmo raciocínio que o
  // próprio backend já usa em AdminService.pareceClienteReal.
  const contaComoAtiva = plano.pago || plano.dataExpiracao != null;
  const hasActivePlan = contaComoAtiva && !isExpired && plano.ativo && plano.planoPreco != null;
  const isCancelled = !hasActivePlan && !isTrial && (isExpired || plano.ativo === false);
  const isCancelledButActive = hasActivePlan && plano.cancelado;

  const nomePlanoExibicao = plano.inconsistente
    ? "Assinatura — Dados Inconsistentes"
    : plano.planoNome && plano.planoNome !== "Plano"
      ? plano.planoNome
      : hasActivePlan
        ? "Assinatura Ativa"
        : "Plano";

  const planoSelecionadoNaTroca = planosDisponiveis.find((p: any) => p.id === selectedPlanoId);
  const trocaEhImediata = !!(
    planoSelecionadoNaTroca &&
    planoSelecionadoNaTroca.preco > (plano.planoPreco ?? 0) &&
    trocaCiclo === plano.ciclo
  );

  // Benefícios do plano atual — reaproveita o mesmo texto já usado na tela
  // de Planos (PLANOS_DATA), casando pelo nome. Se não achar (nome
  // inconsistente/plano legado), a seção some em vez de inventar texto.
  const beneficiosPlanoAtual = plano.planoNome
    ? PLANOS_DATA.find((p) => p.titulo.toLowerCase() === plano.planoNome?.toLowerCase())?.funcionalidades || []
    : [];

  const pagamento = billingTypeInfo(plano.billingType);
  const limiteUsuarios = planLimits?.limiteUsuarios ?? null;
  const usuariosPct = limiteUsuarios && limiteUsuarios > 0 && usuariosCount != null
    ? Math.min(100, Math.round((usuariosCount / limiteUsuarios) * 100))
    : null;

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Minha assinatura</h1>
          <p>Gerencie seu plano, ciclo de cobrança e cancelamento.</p>
        </div>
      </div>

      {isCancelled && (
        <Banner tone="danger">⏰ Sua assinatura foi cancelada ou expirou. <a href="/planos/listagem" style={{ color: "inherit", fontWeight: 700 }}>Assine agora</a> para reativar seu acesso.</Banner>
      )}
      {isTrial && (
        <Banner tone="warning">🕐 Você está em período de teste gratuito. <a href="/planos/listagem" style={{ color: "inherit", fontWeight: 700 }}>Assine agora</a> para continuar usando após o término.</Banner>
      )}
      {plano.inconsistente && (
        <Banner tone="warning">⚠️ Os dados da sua assinatura estão inconsistentes. <a href="/planos/listagem" style={{ color: "inherit", fontWeight: 700 }}>Entre em contato ou reassine</a>.</Banner>
      )}
      {!hasActivePlan && !isTrial && !isCancelled && (
        <Banner tone="warning">⚠️ Seu plano está inativo. <a href="/planos/listagem" style={{ color: "inherit", fontWeight: 700 }}>Ver planos</a></Banner>
      )}
      {isCancelledButActive && (
        <Banner tone="warning">
          🔔 Assinatura cancelada — você mantém acesso completo até <strong>{formatDate(plano.dataExpiracao)}</strong>.{" "}
          <a href="/planos/listagem" style={{ color: "inherit", fontWeight: 700 }}>Reativar assinatura</a>
        </Banner>
      )}

      {hasActivePlan && !plano.cancelado && plano.trocaAgendada && plano.planoFuturoNome && (
        <div className="ds-card" style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "16px 18px", marginBottom: 20, borderLeft: "3px solid var(--orange)" }}>
          <AccessTimeOutlinedIcon fontSize="small" sx={{ color: "var(--orange)", mt: "2px" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13.6 }}>Troca de plano agendada</div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 2 }}>
              <strong>{plano.planoFuturoNome}</strong>
              {plano.cicloFuturo ? ` (${plano.cicloFuturo === "YEARLY" ? "Anual" : "Mensal"})` : ""} será aplicada na próxima renovação, em {formatDate(plano.dataExpiracao)}.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setCancelAgendamentoOpen(true)} style={{ flex: "none" }}>
            Cancelar agendamento
          </Button>
        </div>
      )}

      {hasActivePlan && (
        <>
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              {/* Visão Geral da Assinatura */}
              <div className="ds-card" style={{ padding: 26 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 8 }}>
                  <div>
                    <h2 style={{ fontSize: 20 }}>{nomePlanoExibicao}</h2>
                    {plano.valorFaturado != null ? (
                      <>
                        <div style={{ fontFamily: "var(--font-display)", fontSize: 36, fontWeight: 800, marginTop: 6 }}>
                          {formatCurrency(plano.valorFaturado)}
                          <span style={{ fontSize: 14, fontWeight: 500, color: "var(--ink-faint)", marginLeft: 4 }}>
                            {plano.ciclo === "YEARLY" ? "/ano" : "/mês"}
                          </span>
                        </div>
                        {plano.ciclo === "YEARLY" && (
                          <p style={{ fontSize: 12.6, color: "var(--ink-faint)", marginTop: 4 }}>
                            (total cobrado anualmente — equivalente a {formatCurrency(plano.valorFaturado / 12)}/mês)
                          </p>
                        )}
                      </>
                    ) : plano.planoPreco != null ? (
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 36, fontWeight: 800, marginTop: 6 }}>
                        {formatCurrency(plano.planoPreco)}
                        <span style={{ fontSize: 14, fontWeight: 500, color: "var(--ink-faint)", marginLeft: 4 }}>/mês</span>
                      </div>
                    ) : (
                      <p style={{ marginTop: 6, color: "var(--ink-faint)" }}>Assinatura ativa</p>
                    )}
                  </div>
                  <Badge color={plano.cancelado ? "orange" : "green"}>
                    {plano.cancelado ? "Cancelado (em carência)" : "Ativo"}
                  </Badge>
                </div>

                <div style={{ borderTop: "1px solid var(--border)", margin: "22px 0" }} />

                <div className="ds-plan-meta-grid">
                  {plano.cicloLabel && plano.cicloLabel !== "—" && (
                    <div className="ds-plan-meta-tile">
                      <EventRepeatOutlinedIcon fontSize="small" className="icon" />
                      <div>
                        <div className="label">Ciclo</div>
                        <div className="value">{plano.cicloLabel}</div>
                      </div>
                    </div>
                  )}
                  <div className="ds-plan-meta-tile">
                    <EventAvailableOutlinedIcon fontSize="small" className="icon" />
                    <div>
                      <div className="label">{plano.cancelado ? "Acesso até" : "Próxima renovação"}</div>
                      <div className="value">{formatDate(plano.dataExpiracao)}</div>
                    </div>
                  </div>
                  <div className="ds-plan-meta-tile">
                    {pagamento.icon}
                    <div>
                      <div className="label">Forma de pagamento</div>
                      <div className="value">{pagamento.label}</div>
                    </div>
                  </div>
                </div>
              </div>

              {beneficiosPlanoAtual.length > 0 && (
                <div className="ds-card ds-chart-card" style={{ marginTop: 20 }}>
                  <div className="ds-chart-card-head"><h3>O que está incluso no seu plano</h3></div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px" }}>
                    {beneficiosPlanoAtual.map((b, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13.2, color: "var(--ink-soft)" }}>
                        <CheckCircleOutlineIcon fontSize="small" sx={{ color: "var(--green)", mt: "1px", flex: "none" }} />
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Uso do plano */}
              <div className="ds-card ds-chart-card">
                <div className="ds-chart-card-head"><h3>Uso do plano</h3></div>

                {usuariosCount != null && (
                  <div className="ds-usage-row">
                    <div className="ds-usage-row-head">
                      <span className="label"><PeopleAltOutlinedIcon sx={{ fontSize: 14, verticalAlign: "text-bottom", mr: 0.5, color: "var(--ink-faint)" }} />Usuários ativos</span>
                      <span className="value">{usuariosCount}{limiteUsuarios != null && limiteUsuarios >= 0 ? ` / ${limiteUsuarios}` : " / ilimitado"}</span>
                    </div>
                    {usuariosPct != null && (
                      <div className="ds-usage-track">
                        <div className={`ds-usage-fill ${usuariosPct >= 90 ? "warn" : ""}`} style={{ width: `${usuariosPct}%` }} />
                      </div>
                    )}
                  </div>
                )}

                {cirurgiasMesCount != null && (
                  <div className="ds-usage-row">
                    <div className="ds-usage-row-head">
                      <span className="label"><MonitorHeartOutlinedIcon sx={{ fontSize: 14, verticalAlign: "text-bottom", mr: 0.5, color: "var(--ink-faint)" }} />Cirurgias este mês</span>
                      <span className="value">{cirurgiasMesCount}</span>
                    </div>
                  </div>
                )}

                {usuariosCount == null && cirurgiasMesCount == null && (
                  <p style={{ fontSize: 12.6, color: "var(--ink-faint)" }}>Sem dados de uso disponíveis no momento.</p>
                )}
              </div>

              {/* Faturas recentes */}
              <div className="ds-card ds-chart-card" style={{ marginTop: 20 }}>
                <div className="ds-chart-card-head"><h3>Faturas recentes</h3></div>
                {loadingFaturas ? (
                  <p style={{ fontSize: 12.6, color: "var(--ink-faint)" }}>Carregando...</p>
                ) : faturas.length === 0 ? (
                  <p style={{ fontSize: 12.6, color: "var(--ink-faint)" }}>Nenhuma fatura registrada ainda.</p>
                ) : (
                  faturas.slice(0, 5).map((f) => {
                    const statusInfo = faturaStatusInfo(f.status);
                    return (
                      <div className="ds-invoice-row" key={f.id}>
                        <div>
                          <div className="valor">{formatCurrency(f.valor)}</div>
                          <div className="data">{formatDateHora(f.dataPagamento || f.dataCriacao)}</div>
                        </div>
                        <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
                      </div>
                    );
                  })
                )}
              </div>
            </Grid>
          </Grid>

          {/* Ações da assinatura */}
          <div className="ds-card" style={{ marginTop: 20, padding: 18, display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Button variant="primary" onClick={() => setTrocaDialogOpen(true)}>Trocar de plano / Fazer upgrade</Button>
              <span title="Em breve — ainda não é possível atualizar a forma de pagamento sem trocar de plano.">
                <Button variant="outline" disabled>Atualizar forma de pagamento</Button>
              </span>
            </div>
            {!plano.cancelado && (
              <Button variant="outline" onClick={() => setConfirmOpen(true)}>Cancelar assinatura</Button>
            )}
          </div>
        </>
      )}

      <PagamentoModal
        isOpen={trocaPaymentModalOpen}
        onClose={handleCloseTrocaPaymentModal}
        onConfirm={async () => null}
        planoNome={plano?.planoNome || ""}
        planoPreco={plano?.planoPreco || 0}
        planoCiclo={plano?.ciclo || "MONTHLY"}
        initialPaymentData={trocaPaymentData}
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Cancelar Assinatura</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja cancelar sua assinatura? Você perderá o acesso ao sistema.</Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmOpen(false)} disabled={loadingCancel}>Voltar</MuiButton>
          <MuiButton onClick={handleCancelarAssinatura} color="error" variant="contained" disabled={loadingCancel}>
            {loadingCancel ? "Cancelando..." : "Sim, cancelar assinatura"}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelAgendamentoOpen} onClose={() => setCancelAgendamentoOpen(false)}>
        <DialogTitle>Cancelar Troca Agendada</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja cancelar a troca para <strong>{plano?.planoFuturoNome}</strong>? Seu plano atual permanecerá inalterado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setCancelAgendamentoOpen(false)} disabled={loadingCancelAgendamento}>Voltar</MuiButton>
          <MuiButton onClick={handleCancelarAgendamento} color="warning" variant="contained" disabled={loadingCancelAgendamento}>
            {loadingCancelAgendamento ? "Cancelando..." : "Sim, cancelar agendamento"}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={trocaDialogOpen}
        onClose={() => setTrocaDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: "16px" } } }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 800, fontSize: "1.15rem", pt: 3, pb: 0 }}>
          Trocar de Plano
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          {planosDisponiveis.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 3, mt: 1, gap: 2 }}>
                <Typography sx={{ fontWeight: trocaCiclo === "MONTHLY" ? 700 : 500, color: trocaCiclo === "MONTHLY" ? "var(--ink)" : "var(--ink-faint)", fontSize: "0.9rem" }}>
                  Mensal
                </Typography>
                <Switch
                  checked={trocaCiclo === "YEARLY"}
                  onChange={(_, checked) => setTrocaCiclo(checked ? "YEARLY" : "MONTHLY")}
                />
                <Box sx={{ position: "relative" }}>
                  <Typography sx={{ fontWeight: trocaCiclo === "YEARLY" ? 700 : 500, color: trocaCiclo === "YEARLY" ? "var(--ink)" : "var(--ink-faint)", fontSize: "0.9rem" }}>
                    Anual
                  </Typography>
                  <Typography sx={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", fontSize: "0.6rem", fontWeight: 700, color: "var(--green)", whiteSpace: "nowrap", mt: 0.3 }}>
                    Economize 15%
                  </Typography>
                </Box>
              </Box>

              <div className="ds-plan-pick-label">Selecione o novo plano</div>
              <div className="ds-plan-pick-list" role="radiogroup" aria-label="Selecione o novo plano">
                {planosDisponiveis
                  .filter((p: any) => p.nome && p.preco > 0)
                  .map((p: any) => {
                    // precoAnual e valorMensalEquivalente vem prontos de GET /plano —
                    // a conta do desconto de 15% mora so no backend, junto de quem cobra.
                    const precoExibido = trocaCiclo === "YEARLY" ? p.precoAnual : p.preco;
                    const equivMensal = p.valorMensalEquivalente;
                    const isCurrentPlan = p.id === plano?.planoModeloId;
                    const isSelected = selectedPlanoId === p.id;
                    return (
                      <div
                        key={p.id}
                        className={`ds-plan-pick-card${isSelected ? " selected" : ""}${isCurrentPlan ? " current" : ""}`}
                        onClick={() => !isCurrentPlan && setSelectedPlanoId(p.id)}
                        role="radio"
                        aria-checked={isSelected}
                        aria-disabled={isCurrentPlan}
                        tabIndex={isCurrentPlan ? -1 : 0}
                      >
                        <span className="ds-plan-pick-check">
                          {isSelected ? <CheckCircleOutlineIcon fontSize="small" /> : <span className="ds-plan-pick-dot" />}
                        </span>
                        <div className="ds-plan-pick-info">
                          <div className="name">{p.nome}</div>
                          <div className="price">
                            {formatCurrency(precoExibido)}{trocaCiclo === "YEARLY" ? "/ano" : "/mês"}
                            {trocaCiclo === "YEARLY" && (
                              <span className="ds-plan-pick-equiv"> (equiv. {formatCurrency(equivMensal)}/mês)</span>
                            )}
                          </div>
                        </div>
                        {isCurrentPlan && <Chip label="Seu plano atual" size="small" variant="outlined" color="info" />}
                      </div>
                    );
                  })}
              </div>

              {selectedPlanoId && (
                <Alert severity={trocaEhImediata ? "success" : "info"} sx={{ mt: 2.5 }} icon={<CheckCircleOutlineIcon fontSize="small" />}>
                  {trocaEhImediata
                    ? "Essa troca é aplicada imediatamente — você já passa a ter acesso aos recursos do novo plano agora. Pode ser gerada uma cobrança agora referente apenas à diferença proporcional do período restante (não é uma cobrança cheia nova); sua data de renovação não muda."
                    : `Essa troca entra em vigor na próxima renovação (${formatDate(plano.dataExpiracao)}) — você continua com os benefícios do plano atual até lá, sem cobrança adicional agora.`}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1 }}>
          <Button variant="outline" onClick={() => setTrocaDialogOpen(false)} disabled={loadingTroca}>Voltar</Button>
          <Button variant="primary" onClick={handleTrocarPlano} disabled={!selectedPlanoId || loadingTroca}>
            {loadingTroca ? "Aguardando..." : "Confirmar Troca"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MeuPlanoPage;
